// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Pure functions for document list transformations.
 */

interface DocumentWithGroupId {
  latestVersionAt: string
  groupId?: string | null
}

/**
 * Extract numeric part from groupId (e.g., "ORA-11" -> 11).
 */
function extractGroupIdNumber(groupId: string | null | undefined): number {
  if (!groupId) return 0
  const match = groupId.match(/-(\d+)$/)
  return match ? parseInt(match[1], 10) : 0
}

/**
 * Sort documents by groupId descending (highest numbers first, e.g., ORA-11 before ORA-2).
 */
export function sortDocumentsByGroupId<T extends DocumentWithGroupId>(docs: T[]): T[] {
  return [...docs].sort((a, b) => extractGroupIdNumber(b.groupId) - extractGroupIdNumber(a.groupId))
}

/**
 * Extract unique years from document dates, sorted descending.
 */
export function extractUniqueYears<T extends DocumentWithGroupId>(docs: T[]): number[] {
  const years = new Set<number>()
  for (const doc of docs) {
    years.add(new Date(doc.latestVersionAt).getFullYear())
  }
  return [...years].sort((a, b) => b - a)
}

/**
 * Filter documents by year. Returns all if year is null.
 */
export function filterByYear<T extends DocumentWithGroupId>(docs: T[], year: number | null): T[] {
  if (year === null) return docs
  return docs.filter(doc => new Date(doc.latestVersionAt).getFullYear() === year)
}

/**
 * Build metadata string from file size and upload date.
 */
export function buildMetadataString(fileSize: string | null, uploadedAt: string | null): string {
  const parts = [fileSize, uploadedAt ? `Uploaded ${uploadedAt}` : null].filter(Boolean)
  return parts.join(' • ')
}
